import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useLang } from "@/i18n";
import MechanicProductivity from "@/components/MechanicProductivity";

const COLORS = ["#f97316", "#0ea5e9", "#22c55e", "#a855f7"];

function Kpi({ label, value, hint, testId }) {
  return (
    <Card className="p-5 border-border" data-testid={testId}>
      <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </Card>
  );
}

export default function WorkshopReport({ start, end }) {
  const { t } = useLang();
  const { data: r } = useQuery({ queryKey: ["report-workshop", start, end], queryFn: () => api.get(`/reports/workshop?start=${start}&end=${end}`).then((x) => x.data) });
  if (!r) return null;
  const mix = [
    { name: t("rpLabor"), value: r.mix.labor }, { name: t("rpParts"), value: r.mix.parts },
    { name: t("rpServices"), value: r.mix.services }, { name: t("rpOnsite"), value: r.mix.onsite },
  ].filter((m) => m.value > 0);
  const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" };

  return (
    <div className="space-y-6" data-testid="workshop-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testId="rp-paid" label={t("rpPaidRevenue")} value={formatEUR(r.paid_total)} hint={`${r.paid_count} ${t("rpInvoicesPaid")}`} />
        <Kpi testId="rp-invoiced" label={t("rpInvoiced")} value={formatEUR(r.invoiced_total)} hint={`${r.invoices_count} ${t("invoices").toLowerCase()} · ${t("rpAvg")} ${formatEUR(r.avg_invoice)}`} />
        <Kpi testId="rp-unpaid" label={t("dashUnpaid")} value={formatEUR(r.unpaid_total)} hint={`${r.unpaid_count} ${t("dashUnpaidHint")}`} />
        <Kpi testId="rp-vat" label={t("rpVat")} value={formatEUR(r.vat_total)} hint={`${t("rpNet")} ${formatEUR(r.net_total)}`} />
        <Kpi testId="rp-cards" label={t("rpCardsDone")} value={r.cards_completed} hint={`${r.labor_hours} ${t("rpHours")}`} />
        <Kpi testId="rp-labor" label={t("rpLabor")} value={formatEUR(r.mix.labor)} />
        <Kpi testId="rp-parts" label={t("rpParts")} value={formatEUR(r.mix.parts)} hint={`${t("rpMargin")} ${formatEUR(r.parts_margin)}`} />
        <Kpi testId="rp-services" label={t("rpServices")} value={formatEUR(r.mix.services + r.mix.onsite)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-border">
          <h3 className="font-display text-xl font-bold mb-4">{t("rpRevenuePerDay")}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={r.by_day}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip formatter={(v) => formatEUR(v)} contentStyle={tip} />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6 border-border">
          <h3 className="font-display text-xl font-bold mb-4">{t("rpMix")}</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={mix} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {mix.map((m, i) => <Cell key={m.name || m.key || i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatEUR(v)} contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {mix.map((m, i) => <div key={m.name} className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{m.name} · {formatEUR(m.value)}</div>)}
          </div>
        </Card>
      </div>

      <MechanicProductivity start={start} end={end} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-border overflow-x-auto" data-testid="rp-mechanics">
          <h3 className="font-display text-xl font-bold mb-4">{t("rpPerMechanic")}</h3>
          <Table>
            <TableHeader><TableRow><TableHead>{t("mechanic")}</TableHead><TableHead className="text-right">{t("rpCards")}</TableHead><TableHead className="text-right">{t("rpHours")}</TableHead><TableHead className="text-right">{t("revenue")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {r.mechanics.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t("noData")}</TableCell></TableRow>}
              {r.mechanics.map((m) => <TableRow key={m.name}><TableCell className="font-medium">{m.name}</TableCell><TableCell className="text-right tabular-nums">{m.cards}</TableCell><TableCell className="text-right tabular-nums">{m.hours}</TableCell><TableCell className="text-right font-mono tabular-nums">{formatEUR(m.revenue)}</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </Card>
        <Card className="p-6 border-border overflow-x-auto" data-testid="rp-customers">
          <h3 className="font-display text-xl font-bold mb-4">{t("rpTopCustomers")}</h3>
          <Table>
            <TableHeader><TableRow><TableHead>{t("customer")}</TableHead><TableHead className="text-right">{t("invoices")}</TableHead><TableHead className="text-right">{t("revenue")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {r.top_customers.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t("noData")}</TableCell></TableRow>}
              {r.top_customers.map((c) => <TableRow key={c.name}><TableCell className="font-medium">{c.name}</TableCell><TableCell className="text-right tabular-nums">{c.invoices}</TableCell><TableCell className="text-right font-mono tabular-nums">{formatEUR(c.revenue)}</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </Card>
      </div>

      {r.unpaid_invoices.length > 0 && (
        <Card className="p-6 border-border overflow-x-auto" data-testid="rp-unpaid-list">
          <h3 className="font-display text-xl font-bold mb-4">{t("dashToCollect")}</h3>
          <Table>
            <TableHeader><TableRow><TableHead>{t("invoiceNumber")}</TableHead><TableHead>{t("customer")}</TableHead><TableHead>{t("dueDate")}</TableHead><TableHead className="text-right">{t("amount")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {r.unpaid_invoices.map((i) => <TableRow key={i.invoice_number}><TableCell className="font-mono text-xs">{i.invoice_number}</TableCell><TableCell>{i.customer_name}</TableCell><TableCell className="font-mono text-xs">{i.due_date || "—"}</TableCell><TableCell className="text-right font-mono tabular-nums">{formatEUR(i.total)}</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
